import type { StrategyListItem } from '../../../../../bridge/types/strategy.ts';

import type { OrbPalette } from './orbRenderer';

// The avatar is the strategy's FACE — an agent you recognise across sessions. So identity is a
// pure function of the strategy's place in the owner's history, and every axis below is chosen so
// that nothing the owner does to one strategy changes the face of another.

// Ten identity hues, generated by greedy max-min CIEDE2000 spacing around the wheel rather than
// hand-picked. Anchored on the empty-chat aurora blend + the primary accent (h38 peach↔gold, h197
// sky, h226 accent, h252 lavender, h280 purple), with the semantic bands excised (error red, the
// success greens, the info blue — ±26°) so an identity can never be misread as a status.
//
// The two lightnesses are the ends of a depth ramp, not a light and dark variant of one colour.
// `lNear` is the DARK ink the near edge of the sash is drawn in — it carries the mark, so it is
// what has to clear contrast, and every entry sits far above the 3:1 floor. `lFar` is the
// receding far edge. It is lighter, but only as light as it can be while still reading as the
// same colour: the far side is drawn at reduced alpha too, and letting both go pale at once made
// the bottom of the band fade to nothing.
//
// Ordered so CONSECUTIVE entries are maximally separated (min adjacent ΔE2000 = 23.6, and the
// first three are 56.7 / 29.5 / 49.4 apart): the owner's first strategies are the common case, so
// they differ by the loudest channel available.
const IDENTITY_PALETTE: readonly OrbPalette[] = [
	{ h: 38, s: 68, lFar: 34, lNear: 20, lGlow: 19 },
	{ h: 280, s: 68, lFar: 54, lNear: 26, lGlow: 40 },
	{ h: 211, s: 68, lFar: 46, lNear: 24, lGlow: 32 },
	{ h: 94, s: 68, lFar: 30, lNear: 18, lGlow: 14 },
	{ h: 334, s: 68, lFar: 52, lNear: 25, lGlow: 36 },
	{ h: 226, s: 68, lFar: 54, lNear: 26, lGlow: 41 },
	{ h: 58, s: 68, lFar: 27, lNear: 17, lGlow: 13 },
	{ h: 312, s: 68, lFar: 50, lNear: 25, lGlow: 31 },
	{ h: 197, s: 68, lFar: 36, lNear: 21, lGlow: 20 },
	{ h: 252, s: 68, lFar: 57, lNear: 27, lGlow: 44 },
];

// How many necks travel the loop. Hue alone cannot carry ten identities at 20px, so the silhouette
// carries identity too — and unlike hue, mode survives `prefers-reduced-motion`, where the orb
// freezes on a single necked frame.
const MODES: readonly number[] = [2, 3, 4];

export const IDENTITY_CYCLE = IDENTITY_PALETTE.length * MODES.length * 2;

// Failed keeps the error red that lives outside the identity cycle, so an alarm never reads as
// just another face.
export const FAILED_PALETTE: OrbPalette = {
	h: 0,
	s: 68,
	lFar: 48,
	lNear: 22,
	lGlow: 26,
};

export interface OrbIdentity {
	palette: OrbPalette;
	// Neck count — the silhouette signature.
	mode: number;
	// +1 or -1. Which way the necks travel around the loop.
	spinDir: number;
}

export function identityAt(rank: number): OrbIdentity {
	const i = ((rank % IDENTITY_CYCLE) + IDENTITY_CYCLE) % IDENTITY_CYCLE;
	// Hue and neck count both advance every rank, so neighbouring strategies differ in silhouette
	// as well as colour — stepping the neck count only after the hue cycle ran out left the first
	// ten strategies all wearing the same shape. Ten hues and three neck counts are coprime, and
	// direction flips once per hue cycle, so all sixty faces are used before any repeats.
	return {
		palette: IDENTITY_PALETTE[i % IDENTITY_PALETTE.length],
		mode: MODES[i % MODES.length],
		spinDir: Math.floor(i / IDENTITY_PALETTE.length) % 2 ? -1 : 1,
	};
}

// Ordering key. `started_at` is the only creation-ordered stamp the list projection carries, and it
// never changes after a run begins — unlike the array order, which is the server's `_ts`
// (last-modified) and therefore reshuffles the moment anyone activates a strategy. Ranking on it
// means a new strategy always lands at the end and no existing face moves.
function startedMs(item: StrategyListItem): number {
	const t = Date.parse(item.started_at ?? '');
	// An unparseable stamp sorts first rather than poisoning every comparison with NaN, which would
	// leave the sort order (and so every face) at the mercy of the input order.
	return Number.isNaN(t) ? -Infinity : t;
}

export type OrbIdentityMap = ReadonlyMap<string, OrbIdentity>;

// Faces handed out so far, keyed by strategy id. A face is assigned ONCE and then never changes
// for as long as the app is open.
//
// This is what makes rank safe to compute positionally. Rank is "how many of the owner's
// strategies started before this one", which is stable while the list only grows — but DELETING
// one shifts every later strategy up a place, and recomputing from scratch would hand all of them
// their neighbour's face. Assign-once means a deletion changes nothing for anyone else, and a row
// that has left the list keeps its own face while it collapses out of the dock.
const assigned = new Map<string, OrbIdentity>();

// Faces for the owner's whole history. Always built from the FULL fetched list: a caller holding a
// filtered or curated subset must never compute a rank, or the same strategy would wear different
// faces on two surfaces at once.
export function identityMap(items: StrategyListItem[]): OrbIdentityMap {
	const ordered = [...items].sort(
		(a, b) => startedMs(a) - startedMs(b) || a.id.localeCompare(b.id),
	);
	ordered.forEach((item, rank) => {
		// A strategy already wearing a face keeps it, whatever its rank is today.
		if (!assigned.has(item.id)) assigned.set(item.id, identityAt(rank));
	});
	return new Map(ordered.map((item) => [item.id, assigned.get(item.id)!]));
}

// Cheap, stable hash of an id — a fallback for a strategy no list has ever contained.
function hashRank(id: string): number {
	let h = 0;
	for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
	return Math.abs(h);
}

// The face for one row: the one it was given, or — for a strategy that has never appeared in a
// fetched list — a hash of its id, which is at least stable and its own rather than snapping
// every stranger to one default.
export function identityFor(item: StrategyListItem): OrbIdentity {
	return assigned.get(item.id) ?? identityAt(hashRank(item.id));
}
